import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { uploadImage, formatImageSize, validateImageComprehensive, updateImageDescription } from './utilities/imageUtils';
import { generateImageDescriptions } from '../../AI/utilities/aiLogicService.js';

/**
 * UploadImage Component - Image upload interface with comprehensive validation
 * 
 * This component provides a file upload interface for users to upload new images
 * directly to Firestore as base64-encoded data. Images are stored with a 2MB size limit.
 * 
 * Features:
 *   - File input for selecting images from local system
 *   - Comprehensive validation on file selection:
 *     • File extension check (jpg, jpeg, png, gif, webp)
 *     • MIME type validation
 *     • File size validation (1KB - 2MB)
 *     • Image dimension validation (10px - 10000px)
 *     • File integrity check (verifies it's a valid image)
 *   - Real-time validation feedback with dimension info
 *   - Upload button to send images to Firebase Firestore
 *   - Loading state during upload
 *   - Success/error feedback to user
 *   - Automatic refresh of image gallery after successful upload
 * 
 * Props:
 *   - userId: Current user's unique ID for Firebase operations
 *   - onUploadSuccess: Callback function to refresh gallery after upload
 */
const UploadImage = ({ userId, onUploadSuccess }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    /**
     * Handle file selection with validation
     * Validates file type, size, and dimensions before accepting
     */
    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        setError(null);
        setSuccess(null);

        if (!file) {
            return;
        }

        // Perform comprehensive validation
        setUploading(true);
        try {
            const validation = await validateImageComprehensive(file);

            if (!validation.valid) {
                setError(validation.error);
                fileInputRef.current.value = '';
                setSelectedFile(null);
            } else {
                setSelectedFile(file);
                setSuccess(`Valid image (${validation.width}x${validation.height}px)`);
            }
        } catch (err) {
            setError(`Validation error: ${err.message}`);
            fileInputRef.current.value = '';
            setSelectedFile(null);
        } finally {
            setUploading(false);
        }
    };

    /**
     * Handle image upload to Firestore
     * Re-validates file before upload and saves to Firebase
     */
    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select an image');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            // Final validation before upload
            const finalValidation = await validateImageComprehensive(selectedFile);
            if (!finalValidation.valid) {
                setError(finalValidation.error);
                return;
            }

            let result = null

            if(userId) {
                result = await uploadImage(userId, selectedFile);
            }
            else{
                result = await uploadImage(null, selectedFile);
            }
            

            if (result.success) {
                setSuccess(`Image "${result.data.filename}" uploaded. Generating description...`);
                
                // Now, generate the description and save it
                try {
                    const [describedImage] = await generateImageDescriptions([result.data]);
                    if (describedImage && describedImage.description) {
                        await updateImageDescription(userId, result.imageId, describedImage.description);
                        setSuccess(`Image "${result.data.filename}" uploaded and description generated!`);
                    } else {
                        setSuccess(`Image "${result.data.filename}" uploaded, but could not generate description.`);
                    }
                } catch (aiErr) {
                    console.error('AI Description error:', aiErr);
                    setSuccess(`Image "${result.data.filename}" uploaded, but AI failed.`);
                }

                fileInputRef.current.value = '';
                setSelectedFile(null);

                // Callback to refresh gallery
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(`Upload failed: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="card border-secondary-subtle shadow-sm">
            <div className="card-body">
                <h5 className="card-title mb-3">
                    <i className="bi bi-cloud-upload me-2"></i>
                    Upload New Image
                </h5>
                
                {/* File input */}
                <div className="mb-3">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="form-control"
                        placeholder="Select image"
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={uploading}
                    />
                </div>

                {/* File info */}
                {selectedFile && (
                    <div className="mb-3 p-2 bg-light rounded">
                        <small className="text-muted d-block mb-1">
                            <i className="bi bi-file-earmark me-2"></i>
                            {selectedFile.name}
                        </small>
                        <small className="text-muted d-block">
                            <i className="bi bi-diagram-2 me-2"></i>
                            Size: {formatImageSize(selectedFile.size)}
                        </small>
                    </div>
                )}

                {/* Upload button */}
                <button
                    className="btn btn-primary w-100 mb-3"
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile}
                >
                    {uploading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Uploading...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-upload me-2"></i>
                            Upload Image
                        </>
                    )}
                </button>

                {/* Validation/Upload success message */}
                {success && !error && (
                    <div className="alert alert-info mb-0 alert-sm" role="alert">
                        <small>
                            <i className="bi bi-check-circle me-2"></i>
                            {success}
                        </small>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <div className="alert alert-danger mb-0 alert-sm" role="alert">
                        <small>
                            <i className="bi bi-exclamation-circle me-2"></i>
                            {error}
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
};

UploadImage.propTypes = {
    userId: PropTypes.string.isRequired,
    onUploadSuccess: PropTypes.func,
};

export default UploadImage;